import { type ValidationMode } from '@wix/ambassador-forms-v1-validation-result/types';
import { type Form } from '@wix/ambassador-forms-v4-form/types';
import { formErrorAppearedDissapeared } from '@wix/bi-logger-forms-platform-uou/v2';
import { type Logger } from '@wix/fe-essentials/bi';
import { useAddressForms, useExternalData } from '@wix/form-fields';
import { type ValidationError } from '@wix/form-validator';
import { type RefObject, useCallback } from 'react';

import {
  findErrorByNormalizedPath,
  getFieldErrors,
  mergeErrors,
  validate,
} from '../services';
import { runAdditionalValidation } from '../services/additional-field-validation';
import { type FormValues, type PossibleValues } from '../types';

interface ValidationProps {
  form: Form;
  errorsRef: RefObject<ValidationError[]>;
  setErrors: (errors: ValidationError[]) => void;
  validationMode?: ValidationMode;
}

export const reportErrorChanges = ({
  bi,
  existingErrors,
  newErrors,
  form,
}: {
  bi: Logger;
  existingErrors: ValidationError[];
  newErrors: ValidationError[];
  form: Form;
}) => {
  const newlyAddedErrors = newErrors.filter(
    (newError) =>
      !existingErrors.find(
        (error) =>
          newError.errorPath === error.errorPath &&
          newError.errorType === error.errorType,
      ),
  );
  const removedErrors = existingErrors.filter(
    (error) =>
      !newErrors.find(
        (newError) =>
          error.errorPath === newError.errorPath &&
          error.errorType === newError.errorType,
      ),
  );

  newlyAddedErrors.forEach((error) => {
    const field = form.fields?.find(
      (_field) => _field.target === error.errorPath,
    );
    bi.report(
      formErrorAppearedDissapeared({
        action_type: 'appeared',
        error_name: error.errorMessage,
        field_id: field?.id,
        field_target:
          field?.target && 'target' in field ? field.target : undefined,
        field_name: field?.view?.label,
      }),
    );
  });

  removedErrors.forEach((error) => {
    const field = form.fields?.find(
      (_field) => _field.target === error.errorPath,
    );
    bi.report(
      formErrorAppearedDissapeared({
        action_type: 'disappeared',
        error_name: error.errorMessage,
        field_id: field?.id,
        field_target:
          field?.target && 'target' in field ? field.target : undefined,
        field_name: field?.view?.label,
      }),
    );
  });

  return { newlyAddedErrors, removedErrors };
};

export type HandleFieldValidation = (
  fieldsToValidate: { path: string; value: PossibleValues }[],
) => Promise<ValidationError[]>;
type ValidateFields = (newValues: FormValues) => Promise<ValidationError[]>;
type ValidateStep = (stepId: string, newValues: FormValues) => Promise<boolean>;

type UseValidationReturn = {
  validateFields: ValidateFields;
  validateStep: ValidateStep;
  handleFieldValidation: HandleFieldValidation;
};

export const useValidation = ({
  form,
  errorsRef,
  setErrors,
  validationMode,
}: ValidationProps): UseValidationReturn => {
  const { addressTemplates } = useAddressForms();
  const externalData = useExternalData();

  const validateFields = useCallback(
    async (newValues: FormValues) => {
      // TODO: returns undefined when form-id missing
      const result = await validate(
        form,
        newValues,
        addressTemplates,
        validationMode,
      );
      const _errors = result?.errors || [];

      const additionalErrors = runAdditionalValidation({
        form,
        values: newValues,
        externalData,
      });

      const allErrors = mergeErrors({ errors: _errors, additionalErrors });

      setErrors(allErrors);

      return allErrors;
    },
    [form, setErrors, addressTemplates, externalData, validationMode],
  );

  const handleFieldValidation: HandleFieldValidation = useCallback(
    async (fieldsToValidate: { path: string; value: PossibleValues }[]) => {
      const fieldErrors = await getFieldErrors(
        fieldsToValidate,
        form,
        addressTemplates,
        externalData,
        validationMode,
      );

      const otherErrors = (errorsRef.current ?? []).filter((error) => {
        return !findErrorByNormalizedPath(fieldsToValidate, error.errorPath);
      });

      const allErrors = [...otherErrors, ...fieldErrors];

      setErrors(allErrors);
      return allErrors;
    },
    [
      errorsRef,
      form,
      setErrors,
      addressTemplates,
      externalData,
      validationMode,
    ],
  );
  const validateStep = useCallback(
    async (stepId: string, newValues: FormValues) => {
      const items =
        form?.steps?.find((step) => step.id === stepId)?.layout?.large?.items ||
        [];

      const validationErrors = await Promise.all(
        items.map(async (item): Promise<ValidationError[]> => {
          const field = form?.fields?.find(
            (_field) => _field.id === item.fieldId,
          );

          const fieldValidationResult = field?.target
            ? await handleFieldValidation([
                {
                  path: field.target,
                  value: newValues[field.target],
                },
              ])
            : [];

          return fieldValidationResult;
        }),
      );
      const validationErrorsFlatten = validationErrors.flat();

      setErrors(validationErrorsFlatten);

      return !validationErrorsFlatten.length;
    },
    [form, setErrors, handleFieldValidation],
  );

  return { validateFields, validateStep, handleFieldValidation };
};
